import { GraphQL } from "@rpi/openapi-api";

export const REASON_LABELS: Record<GraphQL.RefundReason, string> = {
  [GraphQL.RefundReason.ColorOutputVariance]: "Color Output Variance",
  [GraphQL.RefundReason.DuplicatePages]: "Duplicate Pages",
  [GraphQL.RefundReason.Ghosting]: "Ghosting",
  [GraphQL.RefundReason.IncorrectSubstrate]: "Incorrect Substrate",
  [GraphQL.RefundReason.MisregistrationFrontToBack]: "Misregistration (front to back)",
  [GraphQL.RefundReason.MisregistrationColorToColor]: "Misregistration (color to color)",
  [GraphQL.RefundReason.MissingPages]: "Missing Pages",
  [GraphQL.RefundReason.SpotsVoidsBandingSmears]: "Spots, Voids, Banding, Smears",
  [GraphQL.RefundReason.WrinkledCreasedPages]: "Wrinkled/Creased Pages",

  [GraphQL.RefundReason.BadSaddleStitching]: "Bad Saddle Stitching",
  [GraphQL.RefundReason.BrokenSpine]: "Broken Spine",
  [GraphQL.RefundReason.CrookedCover]: "Crooked Cover",
  [GraphQL.RefundReason.EndsheetsAlignment]: "Endsheets (alignment)",
  [GraphQL.RefundReason.EndsheetsBubbleWrinkle]: "Endsheets (bubble/wrinkle)",
  [GraphQL.RefundReason.EndsheetsWrong]: "Endsheets (wrong endsheets)",
  [GraphQL.RefundReason.ExcessiveGlueResidue]: "Excessive Glue/Glue Residue",
  [GraphQL.RefundReason.IncorrectDamagedWireO]: "Incorrect/Damaged Wire-O",
  [GraphQL.RefundReason.InkCrackingInGutterLayflat]: "Ink Cracking in Gutter (Layflat)",
  [GraphQL.RefundReason.PagesFallingOut]: "Pages Falling Out",
  [GraphQL.RefundReason.PagesNotGluedCorrectlyLayflat]: "Pages not Glued Correctly (Layflat)",
  [GraphQL.RefundReason.PurGlueSpikes]: "PUR Glue Spikes",
  [GraphQL.RefundReason.SlipsheetBoundInBook]: "Slipsheet or job ticket bound in book block",
  [GraphQL.RefundReason.WavyPages]: "Wavy Pages",
  [GraphQL.RefundReason.WrongLinenType]: "Wrong Linen Type",
  [GraphQL.RefundReason.WrongOrientation]: "Wrong Orientation",
  [GraphQL.RefundReason.WrongPageOrder]: "Wrong Page Order",
  [GraphQL.RefundReason.WrongSpineSize]: "Wrong Spine Size",

  [GraphQL.RefundReason.IncorrectScoreLine]: "Incorrect Score Line",
  [GraphQL.RefundReason.IncorrectFoil]: "Incorrect Foil Used",
  [GraphQL.RefundReason.LaminationBubbles]: "Lamination (bubbles/articles under the Lamination)",
  [GraphQL.RefundReason.LaminationDelamination]: "Lamination (delamination/peeling)",
  [GraphQL.RefundReason.LaminationWrong]: "Lamination (wrong lamination)",
  [GraphQL.RefundReason.PhysicalDamage]: "Physical Damage",
  [GraphQL.RefundReason.TrimmingImageOffCenter]: "Trimming (image off-center)",
  [GraphQL.RefundReason.TrimmingIncorrectDie]: "Trimming (incorrect die)",
  [GraphQL.RefundReason.TrimmingRoughUnclean]: "Trimming (rough/unclean trim)",
  [GraphQL.RefundReason.TrimmingWrongDimensions]: "Trimming (wrong trim dimensions)",
  [GraphQL.RefundReason.UvInconsistent]: "UV (inconsistent UV)",
  [GraphQL.RefundReason.UvMissing]: "UV (missing)",

  [GraphQL.RefundReason.DamageImproperPackaging]: "Damage due to improper packaging",
  [GraphQL.RefundReason.IncorrectQuantity]: "Incorrect Quantity",
  [GraphQL.RefundReason.PackageNotScanned]: "Package Not Scanned",

  [GraphQL.RefundReason.CompleteWrongOrder]: "Complete Wrong Order",
  [GraphQL.RefundReason.CorrectCoverWrongText]: "Correct Cover / Wrong Text",
  [GraphQL.RefundReason.WrongCoverCorrectText]: "Wrong Cover / Correct Text",
  [GraphQL.RefundReason.PartialWrongOrder]: "Partial Wrong Order (additional cards/pages added)",
  [GraphQL.RefundReason.ReceivedAdditionalItems]: "Received Additional Items (their items + another customer's items)",

  [GraphQL.RefundReason.DamagedInTransit]: "Damaged in Transit",
  [GraphQL.RefundReason.LostInTransit]: "Lost in Transit",
  [GraphQL.RefundReason.ReturnedToSender]: "Returned to Sender",

  [GraphQL.RefundReason.NoCredit]: "No Credit",
  [GraphQL.RefundReason.Other]: "Other",
};

export const REASON_GROUPS: readonly { label: string; reasons: GraphQL.RefundReason[] }[] = [
  {
    label: "Print",
    reasons: [
      GraphQL.RefundReason.ColorOutputVariance,
      GraphQL.RefundReason.DuplicatePages,
      GraphQL.RefundReason.Ghosting,
      GraphQL.RefundReason.IncorrectSubstrate,
      GraphQL.RefundReason.MisregistrationFrontToBack,
      GraphQL.RefundReason.MisregistrationColorToColor,
      GraphQL.RefundReason.MissingPages,
      GraphQL.RefundReason.SpotsVoidsBandingSmears,
      GraphQL.RefundReason.WrinkledCreasedPages,
    ],
  },
  {
    label: "Binding",
    reasons: [
      GraphQL.RefundReason.BadSaddleStitching,
      GraphQL.RefundReason.BrokenSpine,
      GraphQL.RefundReason.CrookedCover,
      GraphQL.RefundReason.EndsheetsAlignment,
      GraphQL.RefundReason.EndsheetsBubbleWrinkle,
      GraphQL.RefundReason.EndsheetsWrong,
      GraphQL.RefundReason.ExcessiveGlueResidue,
      GraphQL.RefundReason.IncorrectDamagedWireO,
      GraphQL.RefundReason.InkCrackingInGutterLayflat,
      GraphQL.RefundReason.PagesFallingOut,
      GraphQL.RefundReason.PagesNotGluedCorrectlyLayflat,
      GraphQL.RefundReason.PurGlueSpikes,
      GraphQL.RefundReason.SlipsheetBoundInBook,
      GraphQL.RefundReason.WavyPages,
      GraphQL.RefundReason.WrongLinenType,
      GraphQL.RefundReason.WrongOrientation,
      GraphQL.RefundReason.WrongPageOrder,
      GraphQL.RefundReason.WrongSpineSize,
    ],
  },
  {
    label: "Finishing",
    reasons: [
      GraphQL.RefundReason.IncorrectScoreLine,
      GraphQL.RefundReason.IncorrectFoil,
      GraphQL.RefundReason.LaminationBubbles,
      GraphQL.RefundReason.LaminationDelamination,
      GraphQL.RefundReason.LaminationWrong,
      GraphQL.RefundReason.PhysicalDamage,
      GraphQL.RefundReason.TrimmingImageOffCenter,
      GraphQL.RefundReason.TrimmingIncorrectDie,
      GraphQL.RefundReason.TrimmingRoughUnclean,
      GraphQL.RefundReason.TrimmingWrongDimensions,
      GraphQL.RefundReason.UvInconsistent,
      GraphQL.RefundReason.UvMissing,
    ],
  },
  {
    label: "Shipping",
    reasons: [
      GraphQL.RefundReason.DamageImproperPackaging,
      GraphQL.RefundReason.IncorrectQuantity,
      GraphQL.RefundReason.PackageNotScanned,
    ],
  },
  {
    label: "Not Mine",
    reasons: [
      GraphQL.RefundReason.CompleteWrongOrder,
      GraphQL.RefundReason.CorrectCoverWrongText,
      GraphQL.RefundReason.WrongCoverCorrectText,
      GraphQL.RefundReason.PartialWrongOrder,
      GraphQL.RefundReason.ReceivedAdditionalItems,
    ],
  },
  {
    label: "Courier",
    reasons: [
      GraphQL.RefundReason.DamagedInTransit,
      GraphQL.RefundReason.LostInTransit,
      GraphQL.RefundReason.ReturnedToSender,
    ],
  },
  {
    label: "Customer",
    reasons: [GraphQL.RefundReason.NoCredit, GraphQL.RefundReason.Other],
  },
];

export function buildReasonOptions(): { value: GraphQL.RefundReason; label: string; group: string }[] {
  return REASON_GROUPS.flatMap((group) =>
    group.reasons.map((reason) => ({
      value: reason,
      label: REASON_LABELS[reason],
      group: group.label,
    }))
  );
}

const REASON_LABEL_MAP = new Map<string, string>(Object.entries(REASON_LABELS));

/** Returns the human-readable label for a known reason, or the raw input when unknown. */
export function getReasonLabel(reason: string): string {
  return REASON_LABEL_MAP.get(reason) ?? reason;
}
